import { Stack, Typography } from "@mui/material";
import { Fragment, useEffect, useRef, useState } from "react";
import { BackButton } from "react-common-ui-library/FPACT/Buttons";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  BACK_BUTTON_TEXT,
  FORGOT_PASSWORD_CHALLENGE,
  FORGOT_PASSWORD_ROUTE,
  H1,
  H3,
  IS_RECAPTCHA_ENABLED,
  LOGIN_ROUTE,
  MODE_ALL,
  SPACING_2_5__2,
  USER_VERIFIED_SUCCESSFULLY,
  VERIFY_ACCOUNT_ROUTE,
} from "../../../Constants/CommonConstants";
import {
  FORGOT_PASSWORD_BACK_BUTTON,
  FORGOT_PASSWORD_DESCRIPTION,
  FORGOT_PASSWORD_FORM,
  FORGOT_PASSWORD_HEADER,
} from "../../../Constants/ForgotPasswordConstants";
import { MATCH_NOT_FOUND } from "../../../Constants/ForgotUsernameConstants";
import {
  MONTH_AND_DAY_OF_BIRTH_FIELD,
  USERNAME_FIELD,
} from "../../../Constants/LoginConstants";
import { FLOW_TEXT_ACCOUNT } from "../../../Constants/NoMatchFoundModalConstants";
import {
  SLICE_LOGIN,
  THUNK_POST_FORGOT_PASSWORD,
} from "../../../Constants/SliceAndThunkConstants";
import {
  clearLoginApiResponse,
  postForgotPassword,
  saveForgotPasswordData,
} from "../../../Features/LoginSlice";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { manageLoginSelector } from "../../../Selectors/LoginSelector";
import { StyledTypographyHeader } from "../../../Styles/Common/CommonStyles";
import { StyledForgotPasswordBox } from "../../../Styles/Login/ForgotPassword/ForgotPasswordStyles";
import GoogleReCaptcha from "../../Application/GoogleReCaptcha";
import NoMatchFoundModal from "../../Common/NoMatchFoundModal/NoMatchFoundModal";
import ForgotPasswordFormElements from "./ForgotPasswordFormElements";
import ConfigService from "../../../Services/ConfigService";

const isReCaptchaEnabled = ConfigService(IS_RECAPTCHA_ENABLED);

/**
 * @description Forgot password page that collects username and Month and day of birth and proceed to Reset Password.
 * @returns {ReactNode} Render the username and Month and day of birth inputs with continue action button.
 */
const ForgotPassword = () => {
  const intl = useIntl();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const forgotPasswordRef = useRef();

  const { monthDayOfBirth, userName } = useSelector((state) =>
    manageLoginSelector(state)
  );

  const [isUsernameNotFound, setIsUsernameNotFound] = useState(false);

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(state, SLICE_LOGIN, THUNK_POST_FORGOT_PASSWORD)
  );

  const { flw } = useSelector((state) => manageLoginSelector(state));
  const { isFailure, isSuccess, description } = apiResponses;

  const methods = useForm({
    mode: MODE_ALL,
    defaultValues: {
      [USERNAME_FIELD]: userName,
      [MONTH_AND_DAY_OF_BIRTH_FIELD]: monthDayOfBirth,
    },
  });

  const onBackButtonClickHandler = () => {
    navigate(`/${LOGIN_ROUTE}`);
  };

  const handleOnSubmit = async (data) => {
    const token = isReCaptchaEnabled
      ? await forgotPasswordRef.current.onSubmitWithReCAPTCHA()
      : null;
    if (token || !isReCaptchaEnabled) {
      const payload = {
        userName: data.userName,
        dob: data.monthDayOfBirth,
        fgtPassRecaptchaTkn: token,
      };
      dispatch(
        saveForgotPasswordData({
          monthDayOfBirth: data.monthDayOfBirth,
          userName: data.userName,
        })
      );
      dispatch(postForgotPassword({ data: payload }));
    }
  };

  useEffect(() => {
    if (
      isSuccess &&
      description === USER_VERIFIED_SUCCESSFULLY &&
      flw === FORGOT_PASSWORD_CHALLENGE
    ) {
      navigate(`/${FORGOT_PASSWORD_ROUTE}/${VERIFY_ACCOUNT_ROUTE}`, {
        state: { previousScreen: FORGOT_PASSWORD_ROUTE },
      });
    }
    if (isFailure && description === MATCH_NOT_FOUND) {
      setIsUsernameNotFound(true);
    }
    dispatch(clearLoginApiResponse(THUNK_POST_FORGOT_PASSWORD));
  }, [description, isFailure, isSuccess, navigate, dispatch, flw]);

  return (
    <Fragment>
      <BackButton
        id={FORGOT_PASSWORD_BACK_BUTTON}
        backButtonOnClickHandler={onBackButtonClickHandler}
        backButtonText={intl.formatMessage({ id: BACK_BUTTON_TEXT })}
      />
      <StyledForgotPasswordBox>
        <Stack spacing={SPACING_2_5__2}>
          <StyledTypographyHeader
            id={FORGOT_PASSWORD_HEADER}
            component={H1}
            variant={H3}
          >
            {intl.formatMessage({
              id: FORGOT_PASSWORD_HEADER,
            })}
          </StyledTypographyHeader>
          <Stack spacing={{ xs: 3, md: 5 }}>
            <Typography id={FORGOT_PASSWORD_DESCRIPTION}>
              {intl.formatMessage({
                id: FORGOT_PASSWORD_DESCRIPTION,
              })}
            </Typography>
            <FormProvider {...methods}>
              <form
                id={FORGOT_PASSWORD_FORM}
                onSubmit={methods.handleSubmit(handleOnSubmit)}
              >
                <ForgotPasswordFormElements />
              </form>
            </FormProvider>
          </Stack>
        </Stack>
      </StyledForgotPasswordBox>
      {isReCaptchaEnabled ? <GoogleReCaptcha ref={forgotPasswordRef} /> : null}
      <NoMatchFoundModal
        flow={FLOW_TEXT_ACCOUNT}
        open={isUsernameNotFound}
        setOpen={setIsUsernameNotFound}
      />
    </Fragment>
  );
};

export default ForgotPassword;
